/*
 * Split out of IntegrationsTool when QuickBooks landed under the same
 * filename and the same export name. Both are integrations and both belong
 * on the same screen, so Integrations composes the two rather than either
 * feature winning the file.
 */

import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import {
  disconnectGoogle,
  fetchGmailAccounts,
  startGoogleConnect,
} from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../session";

/**
 * Connecting your own Gmail.
 *
 * Per person, not per company, and that is the point rather than a limitation.
 * A customer replying to an estimate should reach the rep who sent it; mail
 * sent from a shared office address arrives from nobody in particular and gets
 * answered by nobody in particular. So each member of staff connects their own
 * account, and every email this app sends afterwards comes from the person who
 * pressed the button.
 *
 * The whole team's connections are listed, because "why did that customer never
 * get their estimate" is usually answered by "that rep never connected", and
 * that should not need four separate people to check.
 */
export function GmailAccounts() {
  const { profileId, people } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const accounts = useAsync(fetchGmailAccounts, []);
  const rows = accounts.data ?? [];
  const mine = rows.find((row) => row.profile_id === profileId) ?? null;
  const nameFor = (id: string) =>
    people.find((person) => person.id === id)?.full_name ?? t("Somebody");

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      // Navigates away to Google, so there is no success path to handle here.
      await startGoogleConnect();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    setError(null);
    try {
      await disconnectGoogle();
      setConfirmingDisconnect(false);
      await accounts.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!profileId) {
    return (
      <div className="shelf">
        <b>{t("No staff record")}</b>
        {t(
          "Your login is not linked to a staff record yet, so there is nothing to connect a Google account to. An admin can link it under Users.",
        )}
      </div>
    );
  }

  return (
    <>
      <ErrorNote error={error ?? accounts.error} />

      <h4 style={{ margin: "4px 0 8px" }}>Gmail</h4>

      <div className="rrow">
        <span className="k">{t("Your account")}</span>
        <span className="v">
          {mine ? (
            <span className="badge approved">{mine.email_address}</span>
          ) : (
            <span className="badge draft">{t("not connected")}</span>
          )}
        </span>
      </div>

      {mine?.last_error && (
        <div className="err" role="alert" style={{ margin: "8px 0" }}>
          {t(
            "The last email did not send: {error} Connecting again usually fixes it.",
            { error: mine.last_error },
          )}
        </div>
      )}

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
        {mine
          ? t(
              "Emails you send from a job go out from {email}, and replies come straight back to you. They appear in your Gmail Sent folder like any other message.",
              { email: mine.email_address },
            )
          : t(
              "Connect your Google account and the app can send emails as you — the estimate, the handoff, anything from the template list. Until then it can only put them on your clipboard.",
            )}
      </p>

      <p className="na-hint" style={{ maxWidth: 560 }}>
        {t("The app is only ever allowed to")} <b>{t("send")}</b>
        {t(". It cannot read your inbox, and neither can anything else here.")}
      </p>

      {mine ? (
        confirmingDisconnect ? (
          <div className="shelf" style={{ marginTop: 10 }}>
            <b>{t("Disconnect {email}?", { email: mine.email_address })}</b>
            {t(
              "Nothing already sent is affected. You will not be able to send from the app again until you connect.",
            )}
            <div className="actions" style={{ marginTop: 8 }}>
              <button
                className="addrow"
                disabled={busy}
                onClick={() => void disconnect()}
              >
                {t("Disconnect")}
              </button>
              <button
                className="ghost"
                disabled={busy}
                onClick={() => setConfirmingDisconnect(false)}
              >
                {t("Keep it")}
              </button>
            </div>
          </div>
        ) : (
          <div className="actions" style={{ marginTop: 10 }}>
            <button
              className="addrow"
              disabled={busy}
              onClick={() => void connect()}
            >
              {t("Reconnect")}
            </button>
            <button
              className="ghost"
              disabled={busy}
              onClick={() => setConfirmingDisconnect(true)}
            >
              {t("Disconnect")}
            </button>
          </div>
        )
      ) : (
        <button
          className="addrow"
          disabled={busy}
          style={{ marginTop: 10 }}
          onClick={() => void connect()}
        >
          {t("Connect Gmail")}
        </button>
      )}

      <h4 style={{ margin: "22px 0 8px" }}>{t("Who else is connected")}</h4>
      {rows.length === 0 ? (
        <div className="na-hint">{t("Nobody yet.")}</div>
      ) : (
        rows
          .slice()
          .sort((a, b) =>
            nameFor(a.profile_id).localeCompare(nameFor(b.profile_id)),
          )
          .map((row) => (
            <div className="rrow" key={row.profile_id}>
              <span className="k">{nameFor(row.profile_id)}</span>
              <span className="v">
                {row.email_address}
                {row.last_error && (
                  <span className="badge draft" style={{ marginLeft: 8 }}>
                    {t("needs reconnecting")}
                  </span>
                )}
              </span>
            </div>
          ))
      )}
    </>
  );
}
